import api from '@src/api';
import axios from 'axios';
import cloneDeep from 'lodash/cloneDeep';
import uniqueId from 'lodash/uniqueId';

let cancelGetProspectRequest;

export const state = {
  allProspects: [],
  contacts: [],
  selectedProspect: null,
  overriddenCampaignTemplate: null,
  initialImport: false,
  convertedClient: null,
  discardProspectChanges: false,
  hasUnsavedProspectChanges: false,
  prospectConfirmationContext: {},
  isRequestingProspect: false,
};

export const getters = {
  emptyContacts(state) {
    return !state.contacts?.length;
  },
  selectedProspectFullName(state) {
    return `${state.selectedProspect?.firstName} ${state.selectedProspect?.lastName}`;
  },
  prospectsShape(state, getters, rootState, rootGetters) {
    return state.contacts
      .filter((contact) => contact.selected)
      .map((contact) => ({
        org_id: rootGetters['auth/userOrgId'],
        trainer_id: rootGetters['auth/trainerId'],
        firstName: contact.firstName,
        lastName: contact.lastName,
        alias: '',
        preferredName: '',
        connectionOptions: [
          ...contact.phone.map((phone) => ({
            type: 'phone',
            isPreferred: false,
            value: phone,
          })),
          ...contact.email.map((email) => ({
            type: 'email',
            isPreferred: false,
            value: email,
          })),
        ],
        emails: [],
        texts: [],
        unsubscribed: false,
        is_converted: null,
      }));
  },
  prospectsRepliedCount(state) {
    return state.allProspects.filter((prospect) => prospect.unreadCount > 0)
      .length;
  },
  hasUnsavedProspectChanges: (state) => {
    return state.hasUnsavedProspectChanges;
  },
};

export const mutations = {
  setAllProspects(state, value) {
    // sort converted clients last
    const clonedValue = cloneDeep(value);
    clonedValue.sort((a, b) => {
      if (a.is_converted === null) {
        return -1;
      } else if (a.is_converted === b.is_converted) {
        return 0;
      } else {
        return 1;
      }
    });
    state.allProspects = clonedValue;
  },
  setNewProspects(state, value) {
    const newValue = cloneDeep(value);
    state.allProspects.push(...newValue);
  },
  setSelectedProspectRecipients(state, value) {
    state.selectedProspectRecipients = assignSelectedCommsOpt(value);
  },
  setSelectedProspectPreferredConnOpt(state, value) {
    const allProspectsClone = cloneDeep(state.allProspects);
    const i = allProspectsClone.findIndex(
      (prospect) => prospect._id === value.prospect._id
    );
    const j = value.prospect.connectionOptions.findIndex(
      (opt) => opt.value === value.opt.value
    );
    value.prospect.connectionOptions.forEach((opt) => {
      if (opt.isPreferred && opt.value !== value.opt.value) {
        opt.isPreferred = false;
      }
    });
    value.prospect.connectionOptions[j].isPreferred = true;
    state.selectedProspect = value.prospect;
    allProspectsClone[i] = state.selectedProspect;
    state.allProspects = allProspectsClone;
  },
  setTogglePreferredConnOpt(state, value) {
    const { type, opt, i } = value;
    const selectedProspectClone = cloneDeep(state.selectedProspect);
    if (type === 'email') {
      const currentEmailConnOpt = selectedProspectClone.emailConnOpts.find(
        (opt) => opt.isPreferred
      );
      if (currentEmailConnOpt) {
        currentEmailConnOpt.isPreferred = false;
      }
      selectedProspectClone.emailConnOpts[i].isPreferred = !opt.isPreferred;
    } else {
      const currentPhoneConnOpt = selectedProspectClone.phoneConnOpts.find(
        (opt) => opt.isPreferred
      );
      if (currentPhoneConnOpt) {
        currentPhoneConnOpt.isPreferred = false;
      }
      selectedProspectClone.phoneConnOpts[i].isPreferred = !opt.isPreferred;
    }
    const selectedConnOpt = selectedProspectClone.connectionOptions.find(
      (option) => option.value === opt.value && !opt.isPreferred
    );
    if (selectedConnOpt) {
      selectedConnOpt.isPreferred = true;
    }
    state.selectedProspect = selectedProspectClone;
  },
  setOverriddenCampaignTemplate(state, value) {
    state.overriddenCampaignTemplate = value;
  },
  setSelectedProspect(state, value) {
    state.selectedProspect = value;
  },
  setSelectedProspectFirstName(state, value) {
    state.selectedProspect.firstName = value;
  },
  setSelectedProspectLastName(state, value) {
    state.selectedProspect.lastName = value;
  },
  setProspectSelectedCommsOpt(state, value) {
    const allProspectsClone = cloneDeep(state.allProspects);
    const i = allProspectsClone?.findIndex(
      (prospect) => prospect._id === value.prospect._id
    );
    value.type
      ? (allProspectsClone[i].selectedCommsOpt = value.type)
      : delete allProspectsClone[i].selectedCommsOpt;
    state.selectedProspect = allProspectsClone[i];
    state.allProspects = allProspectsClone;
  },
  setUpdateProspectItem(state, value) {
    const allProspectsClone = cloneDeep(state.allProspects);
    const i = allProspectsClone.findIndex(
      (prospect) => prospect._id === value._id
    );
    allProspectsClone[i] = value;
    state.allProspects = allProspectsClone;
  },
  setUpdateSelectedProspectPhoneConnOpt(state, value) {
    const { val, i } = value;
    const selectedProspectClone = cloneDeep(state.selectedProspect);
    if (val === '') {
      const j = selectedProspectClone.connectionOptions.findIndex(
        (connOpt) =>
          connOpt.value === selectedProspectClone.phoneConnOpts[i].value
      );
      selectedProspectClone.connectionOptions.splice(j, 1);
    }
    selectedProspectClone.phoneConnOpts[i].value = val;
    state.selectedProspect = selectedProspectClone;
  },
  setUpdateSelectedProspectEmailConnOpt(state, value) {
    const { val, i } = value;
    const selectedProspectClone = cloneDeep(state.selectedProspect);
    if (val === '' && selectedProspectClone.emailConnOpts.length > 1) {
      const j = selectedProspectClone.emailConnOpts.findIndex(
        (connOpt) =>
          connOpt.value === selectedProspectClone.emailConnOpts[i].value
      );
      selectedProspectClone.connectionOptions.splice(j, 1);
      selectedProspectClone.emailConnOpts.splice(i, 1);
    } else {
      selectedProspectClone.emailConnOpts[i].value = val;
    }
    state.selectedProspect = selectedProspectClone;
  },
  setUpdateSelectedProspectConnOpts(state, value) {
    const selectedProspectClone = cloneDeep(state.selectedProspect);
    const emailConnOpts = selectedProspectClone.emailConnOpts.filter(
      (emailOpt) => emailOpt.value !== ''
    );
    const phoneConnOpts = selectedProspectClone.phoneConnOpts.filter(
      (phoneOpt) => phoneOpt.value !== ''
    );
    selectedProspectClone.connectionOptions = emailConnOpts
      .concat(phoneConnOpts)
      .concat(value);
    state.selectedProspect = selectedProspectClone;
  },
  setOverriddenMultiCampaignTemplate(state, value) {
    const allProspectsClone = cloneDeep(state.allProspects);
    const i = allProspectsClone.findIndex(
      (prospect) => prospect._id === state.selectedProspect._id
    );
    const selectedProspectClone = cloneDeep(state.selectedProspect);
    allProspectsClone[i].message = value;
    selectedProspectClone.message = value;
    state.selectedProspect = selectedProspectClone;
    state.allProspects = allProspectsClone;
    state.overriddenMsgTemplate = value;
  },
  setResetOverriddenMultiCampaignTemplate(state) {
    const allProspectsClone = cloneDeep(state.allProspects);
    const selectedProspectClone = cloneDeep(state.selectedProspect);
    delete selectedProspectClone.message;
    state.selectedProspect = selectedProspectClone;
    state.allProspects = allProspectsClone.map((prospect) => {
      delete prospect.message;
      return prospect;
    });
  },
  setContacts(state, value) {
    if (value) {
      const newValue = cloneDeep(value);
      state.contacts.push({ id: uniqueId('cont_'), ...newValue });
    } else {
      state.contacts = [];
    }
  },
  updateContact(state, value) {
    const { newValue, key, index } = value;
    const contactClone = cloneDeep(state.contacts);
    if (key === 'firstName' || key === 'lastName') {
      // it is a string
      contactClone[index][key] = newValue;
    } else {
      // only first value can be replaced
      contactClone[index][key][0] = newValue;
    }
    state.contacts = contactClone;
  },
  setSelectedContacts(state, value) {
    const { selected, index } = value;
    const contactClone = cloneDeep(state.contacts);
    contactClone[index].selected = selected;
    state.contacts = contactClone;
  },
  selectAllContacts(state) {
    state.contacts.forEach((contact) => {
      contact.selected = true;
    });
  },
  addConflictsArray(state, value) {
    const { index, conflictsArray } = value;
    const contactClone = cloneDeep(state.contacts);
    contactClone[index].conflicts = conflictsArray;
    state.contacts = contactClone;
  },
  setInitialImport(state, value) {
    state.initialImport = value;
  },
  setConvertedClient(state, value) {
    state.convertedClient = value;
  },
  setResetProspectUnreadCount(state) {
    const selectedProspectClone = cloneDeep(state.selectedProspect);
    const allProspectsClone = cloneDeep(state.allProspects);
    const i = allProspectsClone.findIndex(
      (prospect) => prospect._id === selectedProspectClone._id
    );
    if (i > -1) {
      allProspectsClone[i].unreadCount = 0;
    }
    selectedProspectClone.unreadCount = 0;
    selectedProspectClone.emails
      .concat(selectedProspectClone.texts)
      .forEach((msg) => {
        msg.read = true;
      });
    state.allProspects = allProspectsClone;
    state.selectedProspect = selectedProspectClone;
  },
  splitProspectConnOpts(state) {
    const selectedProspectClone = cloneDeep(state.selectedProspect);
    const connOpts = selectedProspectClone.connectionOptions;
    selectedProspectClone.phoneConnOpts = connOpts
      .filter((opt) => opt.type === 'phone')
      .sort((opt) => (opt.isPreferred ? -1 : 1));
    selectedProspectClone.emailConnOpts = connOpts
      .filter((opt) => opt.type === 'email')
      .sort((opt) => (opt.isPreferred ? -1 : 1));
    state.selectedProspect = selectedProspectClone;
  },
  setHasUnsavedProspectChanges(state, value) {
    state.hasUnsavedProspectChanges = value;
  },
  setDiscardProspectChanges(state, value) {
    state.discardProspectChanges = value;
  },
  setProspectConfirmationContext(state, value) {
    state.prospectConfirmationContext = value;
  },
  setIsRequestingProspect(state, value) {
    state.isRequestingProspect = value;
  },
  setCancelGetProspectRequest() {
    if (cancelGetProspectRequest !== undefined) {
      cancelGetProspectRequest('Request cancelled.');
    }
  },
};

export const actions = {
  getAllProspects(ctx) {
    const url = api.URL_GET_ALL_PROSPECTS;
    return axios
      .get(url)
      .then((res) => {
        if (res.data.success) {
          ctx.commit('setAllProspects', res.data.prospects);
        }
      })
      .catch((err) => {
        console.warn(err);
      });
  },

  getProspect(ctx, prospectId) {
    if (prospectId !== undefined) {
      const url = api.URL_GET_PROSPECT.replace(':prospectID', prospectId);
      const CancelToken = axios.CancelToken;
      ctx.commit('setIsRequestingProspect', true);

      return axios
        .get(url, {
          cancelToken: new CancelToken(function executor(cancelFunc) {
            cancelGetProspectRequest = cancelFunc;
          }),
        })
        .then(async (res) => {
          ctx.commit('setIsRequestingProspect', false);
          const path = window.location.pathname;
          const isProspectProfile =
            path.includes('/prospect') && path.includes('/profile');
          await ctx.commit('setSelectedProspect', res.data.prospect);

          if (isProspectProfile) {
            ctx.commit('splitProspectConnOpts');
          }
          return res.data;
        });
    } else {
      console.error(`invalid params (prospectId) : (${prospectId})`);
    }
  },

  createProspects({ commit }, prospects) {
    const url = api.URL_CREATE_PROSPECTS;
    return axios
      .post(url, { prospects })
      .then((res) => {
        if (res.data.success) {
          commit('setNewProspects', res.data.prospects);
          return res.data.success;
        }
      })
      .catch((err) => {
        console.warn(err);
      });
  },

  async updateProspect(ctx, { prospect }) {
    if (prospect.firstName !== undefined) {
      const url = api.URL_UPDATE_PROSPECT.replace(':prospectID', prospect._id);
      let prospectPayload = cloneDeep(prospect); // Clone prospect so later we have access to selectedCommsOpt if it exists
      prospectPayload = await sanitizeProspect(prospectPayload);
      const payload = { prospect: prospectPayload };

      return axios.put(url, payload).then(async (res) => {
        if (prospect.selectedCommsOpt) {
          res.data.prospect.selectedCommsOpt = prospect.selectedCommsOpt;
        }
        ctx.commit('setUpdateProspectItem', res.data.prospect);
        await ctx.commit('setSelectedProspect', res.data.prospect);
        ctx.commit('splitProspectConnOpts');
        ctx.commit('setUpdateProspectItem', ctx.state.selectedProspect);
        return res.data;
      });
    } else {
      console.error(
        `invalid params (prospect.firstName) : (${prospect.firstName})`
      );
    }
  },

  contactProspects(ctx, { campaignId, prospects }) {
    if (campaignId !== undefined && prospects !== undefined) {
      const url = api.URL_CONTACT_PROSPECTS;
      const payload = { campaign: campaignId, prospects };
      return axios.put(url, payload).then((res) => res.data);
    } else {
      console.error(
        `invalid params (campaignId, prospects) : (${campaignId}, ${prospects} must be an array)`
      );
    }
  },

  convertProspect({ commit, dispatch }, prospectId) {
    const url = api.URL_CONVERT_PROSPECTS.replace(':prospectID', prospectId);
    return axios
      .put(url)
      .then((res) => {
        if (res.data.success) {
          commit('setConvertedClient', res.data.client);
          dispatch('getAllProspects');
          return res.data;
        }
      })
      .catch((err) => {
        console.warn(err);
      });
  },

  deleteProspect({ dispatch }, prospectId) {
    const url = api.URL_DELETE_PROSPECT.replace(':prospectID', prospectId);
    return axios
      .delete(url)
      .then((res) => {
        if (res.data.success) {
          dispatch('getAllProspects');
        }
      })
      .catch((err) => {
        console.warn(err);
      });
  },

  verifyProspects({ commit, state, getters, dispatch }, prospects) {
    const url = api.URL_VERIFY_PROSPECTS.concat(
      `?initialImport=${state.initialImport}`
    );
    return axios
      .put(url, { prospects })
      .then(async (res) => {
        if (res.status === 200) {
          if (state.initialImport) {
            res.data.prospects.forEach((prospect) => {
              commit('setContacts', {
                selected: false,
                firstName: prospect.first,
                lastName: prospect.last,
                phone: prospect.phone,
                email: prospect.email,
              });
            });
          } else {
            const conflicts = res.data.prospects.some(
              (prospect) =>
                prospect.existingClientEmailConflicts.length > 0 ||
                prospect.existingClientPhoneConflicts.length > 0 ||
                prospect.existingProspectEmailConflicts.length > 0 ||
                prospect.existingProspectPhoneConflicts.length > 0
            );
            if (conflicts) {
              res.data.prospects.forEach((prospect, index) => {
                const prospectWithConflict =
                  prospect.existingClientEmailConflicts.length > 0 ||
                  prospect.existingClientPhoneConflicts.length > 0 ||
                  prospect.existingProspectEmailConflicts.length > 0 ||
                  prospect.existingProspectPhoneConflicts.length > 0;
                if (prospectWithConflict) {
                  const conflictsArray = [
                    ...prospect.existingClientEmailConflicts,
                    ...prospect.existingClientPhoneConflicts,
                    ...prospect.existingProspectEmailConflicts,
                    ...prospect.existingProspectPhoneConflicts,
                  ];
                  commit('addConflictsArray', { index, conflictsArray });
                }
              });
            } else {
              // only at this point should we create prospects
              const response = await dispatch(
                'createProspects',
                getters.prospectsShape
              );
              return response;
            }
          }
        }
      })
      .catch((err) => {
        console.warn(err);
      });
  },
};

// Private Helpers

function assignSelectedCommsOpt(prospects) {
  return prospects.map((prospect) => {
    const phoneOpts = prospect.connectionOptions.filter(
      (opt) => opt.type === 'phone' && opt.isPreferred
    );
    prospect.selectedCommsOpt = phoneOpts.length > 0 ? 'sms' : 'email';
    return prospect;
  });
}

function sanitizePhoneNumbers(prospect) {
  const prospectClone = cloneDeep(prospect);
  prospectClone.connectionOptions = prospectClone.connectionOptions.map(
    (opt) => {
      if (opt.type === 'phone') {
        opt.value = opt.value.replace(/[\s()+\-.]|ext/gi, '');
      }
      return opt;
    }
  );
  return prospectClone;
}

function sanitizeProspect(prospect) {
  delete prospect.phoneConnOpts;
  delete prospect.emailConnOpts;
  delete prospect.selectedCommsOpt;
  return sanitizePhoneNumbers(prospect);
}
